import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Auth Guard (Functional Guard)
 *
 * Protects routes by checking if user is authenticated.
 * Redirects to AWS Cognito federated sign-in if not authenticated.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'users',
 *   canActivate: [authGuard],
 *   component: UsersListComponent
 * }
 * ```
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);

  return authService.isAuthenticated().pipe(
    map((isAuthenticated) => {
      if (isAuthenticated) {
        return true;
      } else {
        // Not authenticated - redirect to Cognito sign-in
        authService.federatedSignIn();
        return false;
      }
    }),
    catchError((err) => {
      console.error('Auth guard error:', err);
      authService.federatedSignIn();
      return of(false);
    }),
  );
};

/**
 * Auth Guard for Child Routes (Functional Guard)
 *
 * Protects child routes by checking if user is authenticated.
 * Redirects to AWS Cognito federated sign-in if not authenticated.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'users',
 *   canActivateChild: [authGuardChild],
 *   loadChildren: () => import('./features/users/routes').then(m => m.routes)
 * }
 * ```
 */
export const authGuardChild: CanActivateChildFn = (route, state) => {
  return authGuard(route, state);
};
